import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiErrorBody, PageMeta } from '@hrms/shared';
import { ERROR_MESSAGES } from '@hrms/shared';

export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api/v1').replace(/\/$/, '');

/** Normalized API error used across the UI. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
}

export function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  const ax = e as AxiosError<ApiErrorBody>;
  if (ax?.isAxiosError) {
    const body = ax.response?.data;
    if (body && typeof body === 'object' && 'error' in body && body.error) {
      return new ApiError(ax.response!.status, body.error.code, body.error.message, body.error.details, body.requestId);
    }
    if (!ax.response) return new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the server. Check your connection and try again.');
    return new ApiError(ax.response.status, 'HTTP_ERROR', ax.message);
  }
  return new ApiError(0, 'UNKNOWN', e instanceof Error ? e.message : 'Unexpected error');
}

/** Human-readable message for any error (API code → friendly text, validation details flattened). */
export function errorMessage(e: unknown): string {
  const err = toApiError(e);
  if (err.code === 'VALIDATION_FAILED' && Array.isArray(err.details) && err.details.length) {
    return err.details.map(String).join(' · ');
  }
  return err.message || ERROR_MESSAGES[err.code as keyof typeof ERROR_MESSAGES] || 'Something went wrong.';
}

// ───────────────────────── Token handling ─────────────────────────
// The access token lives only in memory. The refresh token is an httpOnly cookie the browser sends
// to /auth/* — JavaScript never sees it. Refreshes are single-flight within a tab and serialized
// across tabs with the Web Locks API so refresh-token rotation never trips reuse detection.

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
const listeners = new Set<(authenticated: boolean) => void>();

export const tokenStore = {
  get: () => accessToken,
  set(token: string | null) {
    accessToken = token;
    listeners.forEach((l) => l(!!token));
  },
  subscribe(fn: (authenticated: boolean) => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export const http = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 60_000,
  headers: { 'X-Client': 'web' },
});

async function doRefresh(): Promise<string | null> {
  const run = async () => {
    try {
      const res = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true, headers: { 'X-HRMS-CSRF': '1', 'X-Client': 'web' } });
      const token = res.data?.data?.accessToken as string | undefined;
      tokenStore.set(token ?? null);
      return token ?? null;
    } catch {
      tokenStore.set(null);
      return null;
    }
  };
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks ? (locks.request('hrms-token-refresh', run) as unknown as Promise<string | null>) : run();
}

export function refreshAccessToken(): Promise<string | null> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

http.interceptors.request.use((config) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);
  return config;
});

http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const code = error.response?.data?.error?.code;
    const isAuthCall = original?.url?.includes('/auth/');
    if (error.response?.status === 401 && code === 'UNAUTHENTICATED' && original && !original._retried && !isAuthCall) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) return http.request(original);
    }
    if (error.response?.status === 401 && !isAuthCall) tokenStore.set(null);
    // Blob responses (downloads) carry the JSON error envelope as a Blob.
    if (error.response?.data instanceof Blob) {
      try {
        (error.response as { data: unknown }).data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep generic error */
      }
    }
    throw toApiError(error);
  },
);

// ───────────────────────── Envelope helpers ─────────────────────────

export interface Page<T> {
  items: T[];
  meta: PageMeta;
}

type Params = Record<string, string | number | boolean | undefined | null>;

function clean(params?: Params) {
  if (!params) return undefined;
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

const unwrap = <T>(r: AxiosResponse<{ data: T }>) => r.data.data;

export const api = {
  get: <T>(url: string, params?: Params) => http.get<{ data: T }>(url, { params: clean(params) }).then(unwrap),
  page: <T>(url: string, params?: Params) =>
    http.get<{ data: T[]; meta: PageMeta }>(url, { params: clean(params) }).then((r) => ({ items: r.data.data, meta: r.data.meta }) as Page<T>),
  post: <T>(url: string, body?: unknown) => http.post<{ data: T }>(url, body ?? {}).then(unwrap),
  put: <T>(url: string, body?: unknown) => http.put<{ data: T }>(url, body ?? {}).then(unwrap),
  patch: <T>(url: string, body?: unknown) => http.patch<{ data: T }>(url, body ?? {}).then(unwrap),
  delete: <T>(url: string, params?: Params) => http.delete<{ data: T }>(url, { params: clean(params) }).then(unwrap),
  upload: <T>(url: string, file: File, fields: Record<string, string | undefined> = {}, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') form.append(k, v);
    return http.post<{ data: T }>(url, form, { onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0) }).then(unwrap);
  },
  /** Download a file (CSV export, document) through the authenticated client. */
  download: async (url: string, params?: Params, fallbackName = 'download') => {
    const res = await http.get<Blob>(url, { params: clean(params), responseType: 'blob' });
    const disposition = String(res.headers['content-disposition'] ?? '');
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const name = match ? decodeURIComponent(match[1]) : fallbackName;
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  },
};
